import { isDefined, isNonEmpty } from '@sgme/fp';

import type { FxCash, FxCashForward, FxCashNdf, FxCashSpot } from '@/store/cash/state.ts';
import type { TradeCaptureInitialized } from '@/store/types.ts';
import type { UserPreferencesState } from '@/store/userPreferences/state.ts';
import { isIsoDate } from '@/utils/date.ts';

export const generateCashSpotTile = (
  currencyPair: string | undefined,
  userPreferences: UserPreferencesState,
  sessionId: string,
): FxCashSpot => {
  const [currency1, currency2] = currencyPair?.split('/') ?? [undefined, undefined];

  const defaultMaturityValue = userPreferences.defaultCashMaturityDate ?? undefined;
  const isMaturityDate = isDefined(defaultMaturityValue) && isIsoDate(defaultMaturityValue);

  const maturityDate = isMaturityDate ? defaultMaturityValue : undefined;
  const maturityDateTenor = !isMaturityDate && isDefined(defaultMaturityValue) ? defaultMaturityValue : undefined;

  const notional =
    isDefined(userPreferences.defaultNotional) && isNonEmpty(userPreferences.defaultNotional)
      ? Number(userPreferences.defaultNotional)
      : undefined;

  return {
    type: 'FxSpot',
    properties: {
      currencyPair,
      currency1,
      currency2,
      maturityDate,
      maturityDateTenor,
      notional: Number.isNaN(notional) ? undefined : notional,
      notionalCurrencyIndex: 1,
    },

    tradeCapture: {
      status: 'INITIALIZED',
      sessionId,
      version: -1,
    },
  };
};

//  ██████╗ ██╗   ██╗ █████╗ ██████╗ ██████╗ ███████╗
// ██╔════╝ ██║   ██║██╔══██╗██╔══██╗██╔══██╗██╔════╝
// ██║  ███╗██║   ██║███████║██████╔╝██║  ██║███████╗
// ██║   ██║██║   ██║██╔══██║██╔══██╗██║  ██║╚════██║
// ╚██████╔╝╚██████╔╝██║  ██║██║  ██║██████╔╝███████║
//  ╚═════╝  ╚═════╝ ╚═╝  ╚═╝╚═╝  ╚═╝╚═════╝ ╚══════╝

export const isCashSpot = (cash: FxCash): cash is FxCashSpot => cash.type === 'FxSpot';
export const isCashForward = (cash: FxCash): cash is FxCashForward => cash.type === 'FxFwd';
export const isCashNdf = (cash: FxCash): cash is FxCashNdf => cash.type === 'FxNdf';

export const buildDuplicatedCashTile = (sourceTile: FxCash, sessionId: string): FxCash => {
  const tradeCapture: TradeCaptureInitialized = {
    status: 'INITIALIZED',
    sessionId,
    version: -1,
  };

  if (isCashForward(sourceTile)) {
    return {
      ...sourceTile,
      properties: { ...sourceTile.properties, rfs: undefined, error: undefined },
      tradeCapture,
    };
  }

  if (isCashSpot(sourceTile)) {
    return {
      ...sourceTile,
      properties: { ...sourceTile.properties, rfs: undefined, margin: undefined, error: undefined },
      tradeCapture,
    };
  }

  // FxNdf
  return {
    ...sourceTile,
    properties: { ...sourceTile.properties, rfs: undefined, margin: undefined, error: undefined },
    tradeCapture,
  };
};
